// Ported from packages/workflow-engine-upstream/ui/src/step-label.ts at commit bebc382; only imports changed.
// How the plan column shows a step (Hamza, 14 Oct 2026). A method step that became several plan
// steps (D1) shows its text once, on its first part; every part then has a short line, built by
// code from what it does, so the same paragraph is not repeated for each read.
import type { Plan } from './contracts';

type Step = Plan['steps'][number];

const AGGREGATES: Record<string, string> = {
  max: 'maximum', min: 'minimum', mean: 'mean', raw: 'raw values', resample: 'values for each interval',
  count_above: 'samples above', excursions: 'time above',
};

const RELATIONS: Record<string, (id: string) => string> = {
  upstream: (id) => `what feeds ${id}`, downstream: (id) => `what ${id} feeds`, part_of: (id) => `what ${id} is part of`,
  controlled_by: (id) => `what controls ${id}`, controls: (id) => `what ${id} controls`, measures: (id) => `what ${id} measures`,
  shown_on: (id) => `the drawings that show ${id}`,
};

const day = (iso: string) => iso.slice(0, 10);

/** One short line for what a step does: "maximum of U1-FT-0001, 2025-06-01 to 2025-11-30". */
export function stepAction(st: Step): string {
  const op = st.op as Record<string, unknown> | undefined;
  if (op?.op === 'read_series') {
    const agg = String(op.aggregate ?? '');
    const what = agg === 'count_above' || agg === 'excursions' ? `${AGGREGATES[agg]} ${String(op.threshold)}` : AGGREGATES[agg] ?? agg;
    const period = op.period as { from: string; to: string } | undefined;
    return `${what} of ${(op.tags as string[]).join(', ')}${period ? `, ${day(period.from)} to ${day(period.to)}` : ''}`;
  }
  if (op?.op === 'follow_relation') {
    const id = String((op.from as { id?: string })?.id ?? '');
    return `${(RELATIONS[String(op.relation)] ?? ((x: string) => `${String(op.relation).replace(/_/g, ' ')} of ${x}`))(id)}`;
  }
  if (op?.op === 'find_docs') return 'search the documents';
  if (op?.op === 'db_ask') return 'query the records';
  if (st.kind === 'calc') {
    const kind = st.bindings?.limitType;
    return kind && kind !== 'none' ? `compare with the ${kind === 'open' ? '' : `${kind} `}limit` : 'work out from the reads above';
  }
  return stepKind(st);
}

const KIND_WORDS: Record<Step['kind'], string> = {
  triage: 'sorts the question', gather: 'reads data', calc: 'works out', reason: 'reasons over what was read', decide: 'decides what runs next', ask: 'asks you', deliver: 'writes the answer',
};
const OP_WORDS: Record<string, string> = {
  resolve_assets: 'finds the tags', follow_relation: 'follows a link', find_docs: 'searches the documents', read_series: 'reads the historian', db_ask: 'reads the records',
};

/** What kind of step it is, in words: "reads the historian", "works out". */
export const stepKind = (st: Step): string => (st.op ? OP_WORDS[st.op.op] : undefined) ?? KIND_WORDS[st.kind] ?? 'a step';

/** The step's heading (its text, or null for a later part of the same method step) and its short line, if it is one part of several. */
export function stepLabel(plan: Plan, st: Step): { heading: string | null; part: string | null } {
  const group = st.sourceStep ? plan.steps.filter((x) => x.sourceStep === st.sourceStep) : [st];
  if (group.length < 2) return { heading: st.title, part: null };
  const first = group[0].id === st.id;
  return { heading: first ? st.title : null, part: `${group.indexOf(st) + 1} of ${group.length}: ${stepAction(st)}` };
}
